import React, { useRef, useState, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import lang from '@/lang'
import SupplierCustomerSelect from '@/modules/base/components/generics/supplier-customer-select/supplier-customer-select'
import { USER_RIGHTS, SUBSCRIPTION_MODULES, customerTypes } from '@/modules/base/util/constants'
import { BASE_DATA_TYPES } from '@/modules/base/components/generics/constants'
import checkAccessAndRights from '@/modules/_helpers/checkAccessAndRights'
import { useDispatch } from 'react-redux'
import { updateDocument } from '@/redux/ducks/document/drawer'
import { Link } from 'react-router'

const useStyles = makeStyles(() => ({
  sub: {
    fontSize: 12,
  },
  link: {
    marginTop: 12,
    lineHeight: '14px',
  },
  select: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}))

const getCustomerSupplierOption = (businessPartner, type) => {
  if (!businessPartner?.id) {
    return null
  }

  return {
    value: businessPartner.id,
    label: businessPartner.name,
    labelPrefix: businessPartner.number,
    type,
    data: {
      ...businessPartner,
      baseDataId: businessPartner.id,
      type,
    },
  }
}

const CustomerSupplierInfo = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles(props)

  const { file, customClass, customerSupplierFormData = {}, readOnly = false } = props
  const { customer, supplier, isAssigned, metaData } = file

  const hasSupplierRights = checkAccessAndRights(SUBSCRIPTION_MODULES.SUPPLIERS, USER_RIGHTS.SUPPLIERS.WRITE)
  const hasCustomerRights = checkAccessAndRights(SUBSCRIPTION_MODULES.CUSTOMERS, USER_RIGHTS.CUSTOMERS.WRITE)

  const ref = useRef()
  const [customerSupplier, setCustomerSupplier] = useState(undefined)
  const [inputValue, setInputValue] = useState(undefined)
  const [initialInputValue, setInitialInputValue] = useState(undefined)
  const [formData, setFormData] = useState(customerSupplierFormData)
  const [isOcrValue, setIsOcrValue] = useState(false)

  const route = `/${customer ? BASE_DATA_TYPES.CUSTOMER : BASE_DATA_TYPES.SUPPLIER}`
  const showNavigateTo = (customerSupplier && (customer && hasCustomerRights)) || (customerSupplier && (supplier && hasSupplierRights))

  useEffect(() => {
    if (customer && customer.name) {
      setCustomerSupplier(getCustomerSupplierOption(customer, BASE_DATA_TYPES.CUSTOMER))
      setInputValue('')
      return
    }

    if (supplier && supplier.name) {
      setCustomerSupplier(getCustomerSupplierOption(supplier, BASE_DATA_TYPES.SUPPLIER))
      setInputValue('')
      return
    }

    const ocrPayee = metaData?.ocr?.payee
    if (ocrPayee) {
      setInputValue(ocrPayee)
      setInitialInputValue(ocrPayee)
    } else if (file.payee) {
      setInputValue(file.payee)
      setInitialInputValue(file.payee)
    } else {
      setCustomerSupplier(null)
      setInputValue('')
    }
  }, [file.id, file.supplier?.id, file.customer?.id, file.payee, metaData?.ocr?.payee])

  useEffect(() => {
    if (metaData?.ocr) {
      if (!customer && !supplier) {
        setFormData({
          ...customerSupplierFormData,
          companyName: metaData.ocr.payee,
          street: metaData.ocr.payeeStreet,
          city: metaData.ocr.payeeCity,
          zipCode: metaData.ocr.payeeZipCode,
          countryIso: metaData.ocr?.payeeAddress?.countryIso,
        })
        setIsOcrValue(true)
      } else {
        setIsOcrValue(false)
      }
    }
  }, [metaData?.ocr, customer, supplier])

  const handleChangeSupplierCustomer = (contact) => {
    setInitialInputValue(null)
    setInputValue('')
    setCustomerSupplier(contact)
    const contactData = contact?.data
    const normalizedType = contactData?.type ?? contact?.type
    const normalizedId = contactData?.id ?? contactData?.baseDataId
    const { CUSTOMER, SUPPLIER } = BASE_DATA_TYPES

    if (!contactData || !normalizedType || !normalizedId) {
      dispatch(updateDocument({ key: 'customer', value: null }))
      dispatch(updateDocument({ key: 'supplier', value: null }))
      setCustomerSupplier(null)
      setFormData({})
      setInputValue('')
      setIsOcrValue(false)
    } else {
      const isCustomer = normalizedType === CUSTOMER
      const key = isCustomer ? CUSTOMER : SUPPLIER
      const resetKey = isCustomer ? SUPPLIER : CUSTOMER
      const value = { ...contactData, id: normalizedId, type: normalizedType }

      dispatch(updateDocument({ key: resetKey, value: null }))
      dispatch(updateDocument({ key, value }))
    }
  }

  const handleCreateOptionClick = () => {
    setInitialInputValue(inputValue)
    setFormData(prev => ({
      ...prev,
      companyName: inputValue,
    }))
    setIsOcrValue(false)
  }

  const handleModalClose = () => {
    setInputValue(initialInputValue)
    if (metaData?.ocr?.payee && initialInputValue === metaData.ocr.payee) {
      setIsOcrValue(true)
    }
  }

  const id = customerSupplier?.data?.id ?? customerSupplier?.data?.baseDataId

  const shouldShowHighlight = !!metaData?.ocr?.payee && !customer && !supplier && isOcrValue

  if (isAssigned) {
    if (!(customer || supplier)) {
      return null
    }

    const { id, kind, name, number, addresses = [{}] } = customer ?? supplier

    const [{
      street, zipCode, city,
    }] = addresses

    const customerKind = kind === customerTypes.PERSON
      ? lang.generics.private
      : lang.generics.company

    const label = customer ? lang.booking.assignmentDetails.customer : lang.supplierLabel

    return (
      <div className={customClass}>
        <label>{label}</label>
        <div>
          <span className={classes.sub}>{customerKind} | Nr. {number}</span>
          <span>{name}</span>
          <span>{street}</span>
          <span>{zipCode} {city}</span>
          {showNavigateTo && <span className={classes.link}><Link to={`${route}/${id}`}>{lang.documents.showDetails}</Link></span>}
        </div>
      </div>
    )
  }

  return (
    <div className={classes.select}>
      <SupplierCustomerSelect
        ref={ref}
        name='payee'
        label={`${lang.receipt.form.payee.label}`}
        placeholder={lang.receipt.form.payee.placeholder}
        loadingMessage={() => lang.receipt.form.payee.loadingMessage}
        value={customerSupplier}
        inputValue={inputValue}
        onInputChange={setInputValue}
        handleAdd={handleChangeSupplierCustomer}
        handleChange={handleChangeSupplierCustomer}
        customerSupplierFormData={formData}
        onCreate={handleCreateOptionClick}
        onModalClose={handleModalClose}
        showFillAnimation={shouldShowHighlight}
        readOnly={readOnly}
      />
      {showNavigateTo && <span className={classes.link}><Link to={`${route}/${id}`}>{lang.documents.showDetails}</Link></span>}
    </div>
  )
}

CustomerSupplierInfo.propTypes = {
  file: PropTypes.object.isRequired,
  customerSupplierFormData: PropTypes.object,
  customClass: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChangeSupplierCustomer: PropTypes.func,
  readOnly: PropTypes.bool,
}

export default CustomerSupplierInfo
